import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, FileDown, CalendarCheck } from 'lucide-react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { LogoPrixia } from '@/components/layout/logo-prixia';
import { footerColumns } from '../data/landing-data';
import { useBookDemoStore } from '../store/book-demo-store';

function FooterLink({ href, label }: { href: string; label: string }) {
  const navigate = useNavigate();
  const isExternal =
    /^https?:\/\//.test(href) || href.startsWith('mailto:') || href.endsWith('.html');
  if (isExternal) {
    return (
      <a
        href={href}
        target={href.endsWith('.html') ? '_blank' : undefined}
        rel="noopener noreferrer"
        className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
      >
        {label}
      </a>
    );
  }
  return (
    <a
      href={href}
      onClick={(e) => {
        e.preventDefault();
        if (href.startsWith('#')) {
          const el = document.querySelector(href);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
          } else {
            navigate('/' + href);
          }
        } else {
          navigate(href);
        }
      }}
      className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
    >
      {label}
    </a>
  );
}

export function CtaFooterSection() {
  const openDemo = useBookDemoStore((s) => s.open);
  return (
    <>
      {/* CTA */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="relative overflow-hidden rounded-2xl">
              {/* Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-violet-600 to-blue-700" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.15),_transparent_50%)]" />

              <div className="relative px-8 py-16 sm:px-16 sm:py-20 text-center">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  ¿Listo para dejar Excel atrás?
                </h2>
                <p className="text-white/70 max-w-xl mx-auto mb-8">
                  Cotizaciones, pricing dinámico e importaciones — todo en una plataforma. 30
                  minutos por Zoom y te mostramos Prixia con datos de tu industria.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <button
                    onClick={() => openDemo()}
                    className="group flex items-center gap-2 px-8 py-3.5 rounded-xl bg-white text-blue-700 font-semibold hover:bg-white/90 transition-all hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]"
                  >
                    <CalendarCheck className="w-4 h-4" />
                    Solicitar demo
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <a
                    href="/prixia-pricing-deck.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2 px-8 py-3.5 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-all"
                  >
                    <FileDown className="w-4 h-4" />
                    Ver deck comercial
                  </a>
                  <Link
                    to="/login"
                    className="flex items-center gap-2 px-8 py-3.5 rounded-xl border border-white/20 text-white hover:bg-white/10 transition-all"
                  >
                    Iniciar Sesión
                  </Link>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200/70 dark:border-gray-800 py-12 sm:py-16 bg-white/50 dark:bg-gray-950/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-4">
                <LogoPrixia className="h-9 w-auto" />
              </Link>
              <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                La plataforma de pricing inteligente para empresas de Latam.
              </p>
            </div>

            {/* Link columns */}
            {footerColumns.map((col) => (
              <div key={col.title}>
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                  {col.title}
                </h4>
                <ul className="space-y-2">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <FooterLink href={link.href} label={link.label} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Copyright */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              &copy; {new Date().getFullYear()} Prixia. Todos los derechos reservados.
            </p>
            <div className="flex items-center gap-4">
              <Link
                to="/privacy"
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Privacidad
              </Link>
              <Link
                to="/terms"
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                Términos
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
